import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import {
  SubscriptionUpsellFallbackCondition,
  TrackingPageSubscriptionUpsellBlock,
} from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { ShopifyGraphQLProduct } from "@redotech/redo-model/product";
import { TrackableType } from "@redotech/redo-model/trackable";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { memo, useContext, useEffect, useState } from "react";
import { toast } from "../../alert";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { CurrencyContext } from "../../currency";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import { Promotion } from "./promotion";
import {
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
  getHeaderStyles,
} from "./shared/block-styles";
import { BlockWrapper } from "./shared/block-wrapper";

export const SubscriptionUpsell = memo(function SubscriptionUpsell(
  props: TrackingPageSubscriptionUpsellBlock,
) {
  const {
    title,
    subscriptionProductId,
    orderIntervalFrequency,
    chargeIntervalFrequency,
    isEditingFallback,
    fallbackBlockSettings,
    fallbackCondition,
    perks,
    descriptionOverride,
    buttonSettings,
    dropdownSettings,
  } = props;
  const context = useRequiredContext(TrackingPageContext);
  const { pageSettings, environment, order, productUpsell, returnAppSettings } =
    context;

  const subscriptionUpsell =
    environment === TrackingPageEnvironment.CUSTOMER_PORTAL
      ? context.subscriptionUpsell
      : undefined;

  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const { formatCurrency: priceFormatter } = useContext(CurrencyContext);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, string>
  >({});
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);

  const productLoad = useLoad<ShopifyGraphQLProduct | null>(async () => {
    if (!subscriptionProductId) return null;
    const result = await productUpsell?.getProductById?.({
      productId: subscriptionProductId,
    });
    if (!result) return null;
    return result;
  }, [subscriptionProductId]);

  // Fetch Recharge plans for the product
  const plansLoad = useLoad(async () => {
    if (!subscriptionProductId || !subscriptionUpsell?.getSubscriptionPlans)
      return null;

    try {
      const result = await subscriptionUpsell.getSubscriptionPlans({
        productId: subscriptionProductId,
      });
      return result.plans;
    } catch (error) {
      console.error("Failed to fetch Recharge plans:", error);
      return [];
    }
  }, [subscriptionProductId, subscriptionUpsell]);

  // Set initial selected variant when product loads
  useEffect(() => {
    if (productLoad.value?.variants.nodes.length) {
      const firstAvailableVariant = productLoad.value.variants.nodes.find(
        (variant) => variant.availableForSale,
      );
      if (firstAvailableVariant && !selectedVariantId) {
        setSelectedVariantId(firstAvailableVariant.id);
        // Initialize selected options from first variant
        const initialOptions: Record<string, string> = {};
        if (firstAvailableVariant.selectedOptions)
          for (const option of firstAvailableVariant.selectedOptions) {
            initialOptions[option.name] = option.value;
          }
        setSelectedOptions(initialOptions);
      }
    }
  }, [productLoad.value, selectedVariantId]);

  // Set initial selected plan when plans load
  useEffect(() => {
    if (plansLoad.value && plansLoad.value.length > 0 && !selectedPlanId) {
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setSelectedPlanId(plansLoad.value[0].id);
    }
  }, [plansLoad.value, selectedPlanId]);

  // Update selected variant when options change
  useEffect(() => {
    if (
      productLoad.value?.variants.nodes &&
      Object.keys(selectedOptions).length > 0
    ) {
      const matchingVariant = productLoad.value.variants.nodes.find(
        (variant) => {
          return variant.selectedOptions?.every(
            (option) => selectedOptions[option.name] === option.value,
          );
        },
      );
      if (matchingVariant && matchingVariant.availableForSale) {
        setSelectedVariantId(matchingVariant.id);
      }
    }
  }, [selectedOptions, productLoad.value]);

  const selectedVariant = productLoad.value?.variants.nodes.find(
    (variant) => variant.id === selectedVariantId,
  );

  // Extract unique option types from variants
  const optionTypes = productLoad.value?.options || [];

  const createSubscription = useHandler(async () => {
    if (
      !subscriptionProductId ||
      !order?.customer.email ||
      !selectedVariantId
    ) {
      throw new Error(
        "Missing subscription product, customer email, or selected variant",
      );
    }

    const customerEmail = order.customer.email;
    const numericVariantId = parseNumericIdFromShopifyId(selectedVariantId);

    const result = await subscriptionUpsell?.createSubscription?.({
      productId: subscriptionProductId,
      variantId: numericVariantId?.toString() || selectedVariantId,
      customerEmail,
      quantity: 1,
      orderIntervalFrequency,
      orderIntervalUnit: "month",
      chargeIntervalFrequency,
      customerFirstName: order.customer.firstName ?? undefined,
      customerLastName: order.customer.lastName ?? undefined,
      address1: order.shippingAddress?.streetAddress ?? undefined,
      city: order.shippingAddress?.city ?? undefined,
      province: order.shippingAddress?.state ?? undefined,
      zip: order.shippingAddress?.postalCode ?? undefined,
      countryCode: order.shippingAddress?.countryCode ?? undefined,
      orderId: order.internalMongoId,
    });

    return result;
  });

  const handleStartSubscription = async () => {
    // If customer doesn't exist, redirect to checkout with subscription
    if (!subscriptionCheckLoad.value?.customerExists) {
      if (!selectedVariantId || !selectedPlanId) {
        toast("Please select a variant and subscription plan", {
          variant: "error",
        });
        return;
      }

      // Get the selected plan details
      const selectedPlan = plansLoad.value?.find(
        (plan) => plan.id === selectedPlanId,
      );
      if (!selectedPlan) {
        toast("Invalid subscription plan selected", { variant: "error" });
        return;
      }

      // Get the numeric variant ID from the global ID
      const numericVariantId = parseNumericIdFromShopifyId(selectedVariantId);

      if (
        order &&
        productUpsell &&
        environment === TrackingPageEnvironment.CUSTOMER_PORTAL
      ) {
        await productUpsell.logPageClick({
          trackableId: order.internalMongoId,
          trackableType: TrackableType.ORDER,
          eventType: "subscription",
          url:
            returnAppSettings.storeUrl +
            "/products/" +
            productLoad.value?.handle,
        });

        // Use redirectToWebsiteWithDiscountCode to redirect to product page
        productUpsell.redirectToWebsiteWithDiscountCode({
          settings: returnAppSettings,
          window: productUpsell.selectWindow,
          selectedProduct: {
            url: productLoad.value?.handle || "",
            variantId: numericVariantId || "",
            selectedPlanId: selectedPlan.sellingPlanId || undefined,
          },
          utmSource: "redo",
          utmMedium: "order_tracking",
          utmContent: "subscription_upsell",
          utmTerm: order.internalMongoId,
        });
        return;
      }
    }

    // Existing customer - create subscription through API
    setIsCreatingSubscription(true);
    try {
      const result = await createSubscription();
      if (result && result.success) {
        // Handle successful subscription creation
        setSubscriptionCreated(true);
        toast(
          "Subscription created successfully! You'll receive an email confirmation shortly.",
          { variant: "success" },
        );
      } else {
        toast("Failed to create subscription. Please try again.", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast(
        error instanceof Error
          ? error.message
          : "An error occurred while creating your subscription. Please try again.",
        { variant: "error" },
      );
    } finally {
      setIsCreatingSubscription(false);
    }
  };

  // Check if customer is subscribed
  const subscriptionCheckLoad = useLoad(async () => {
    if (
      order?.customer?.email &&
      subscriptionUpsell?.checkCustomerSubscription
    ) {
      return await subscriptionUpsell.checkCustomerSubscription(
        order.customer.email,
        productLoad.value?.variants.nodes.map((variant) => variant.id) || [],
      );
    }
    return null;
  }, [order?.customer?.email, selectedVariantId, subscriptionUpsell]);

  // Subscription providers (Recharge, Skio) identify customers by email,
  // so we can't create or check subscriptions without one.
  if (
    environment !== TrackingPageEnvironment.BUILDER &&
    !order?.customer?.email
  ) {
    return null;
  }

  // Determine if we should show fallback
  const shouldShowFallback =
    subscriptionCheckLoad.value?.customerExists &&
    fallbackCondition ===
      SubscriptionUpsellFallbackCondition.CUSTOMER_IS_SUBSCRIBED &&
    subscriptionCheckLoad.value?.isSubscribedToProduct;

  const isLoading = productLoad.pending;
  const hasMultipleVariants =
    productLoad.value?.variants.nodes?.length &&
    productLoad.value.variants.nodes.length > 1;
  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const buttonStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    buttonSettings,
  );
  const buttonHierarchy = getButtonHierarchy(buttonSettings);
  const dropdownStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    dropdownSettings,
  );
  const buttonBorderRadius = getButtonBorderRadius(pageSettings);
  const bodyFontFamily = pageSettings?.typography.bodyFontFamily;

  // If we're editing the fallback, render the promotion block instead
  if (
    ((shouldShowFallback && environment !== TrackingPageEnvironment.BUILDER) ||
      isEditingFallback) &&
    fallbackBlockSettings
  ) {
    return <Promotion {...fallbackBlockSettings} />;
  }

  // Show success state if subscription was created
  if (subscriptionCreated && environment !== TrackingPageEnvironment.BUILDER) {
    return (
      <BlockWrapper
        environment={environment}
        loading={false}
        pageSettings={pageSettings}
        withBorder
        withScrollbar
      >
        <Flex
          align="center"
          dir="column"
          gap="xl"
          height="full"
          justify="center"
          p="3xl"
        >
          <CheckCircleIcon
            height={48}
            style={{ color: "#22C55E" }}
            width={48}
          />
          <Flex align="center" dir="column" gap="md">
            <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
              Subscription Created!
            </Text>
            <Text fontSize="sm" style={bodyStyles} textAlign="center">
              Thank you for subscribing! You'll receive an email confirmation
              with your subscription details shortly.
            </Text>
          </Flex>
        </Flex>
      </BlockWrapper>
    );
  }

  return (
    <BlockWrapper
      environment={environment}
      loading={isLoading}
      pageSettings={pageSettings}
      withBorder
      withScrollbar
    >
      <Flex dir="column" gap="xl" height="full" justify="space-between" p="3xl">
        <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
          {title}
        </Text>
        <Flex dir="row" gap="lg">
          <Flex dir="column" flex={1} gap="md">
            <img
              alt={productLoad.value?.title}
              height={180}
              src={
                selectedVariant?.image?.url ??
                productLoad.value?.images.nodes[0]?.url ??
                "https://placehold.co/180x180"
              }
              style={{
                borderRadius: "10px",
                objectFit: "cover",
                border: "1px solid #FEFEFE",
              }}
              width={180}
            />
          </Flex>
          <Flex dir="column" flex={1} gap="2xl">
            {subscriptionProductId && (
              <>
                <Flex dir="column" gap="sm">
                  <Text fontSize="sm" fontWeight="semibold" style={bodyStyles}>
                    {productLoad.value?.title}
                  </Text>
                  {(descriptionOverride || productLoad.value?.description) && (
                    <Text fontSize="sm" style={bodyStyles}>
                      {descriptionOverride ||
                        (productLoad.value?.description &&
                        productLoad.value?.description.length > 120
                          ? `${productLoad.value?.description.substring(0, 120)}...`
                          : productLoad.value?.description)}
                    </Text>
                  )}
                  {/* Selected variant price */}
                  {selectedVariant && (
                    <Text fontSize="sm" fontWeight="medium" style={bodyStyles}>
                      {priceFormatter(selectedVariant.price)}
                    </Text>
                  )}
                </Flex>

                {/* Variant Selection */}
                {environment !== TrackingPageEnvironment.BUILDER &&
                  hasMultipleVariants &&
                  optionTypes.length > 0 && (
                    <Flex dir="column" gap="md">
                      {optionTypes.map((optionType) => {
                        // Get unique values for this option type
                        const optionValues = Array.from(
                          new Set(
                            productLoad.value?.variants.nodes
                              .filter((v) => v.availableForSale)
                              .flatMap(
                                (variant) =>
                                  variant.selectedOptions
                                    ?.filter(
                                      (opt) => opt.name === optionType.name,
                                    )
                                    .map((opt) => opt.value) || [],
                              ),
                          ),
                        );

                        return (
                          <Flex dir="column" gap="xs" key={optionType.name}>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              style={bodyStyles}
                            >
                              {optionType.name}
                            </Text>
                            <Flex dir="row" gap="xs" wrap="wrap">
                              {optionValues.map((value) => {
                                const isSelected =
                                  selectedOptions[optionType.name] === value;
                                return (
                                  <RedoButton
                                    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                                      ...buttonStyles,
                                      ...buttonBorderRadius,
                                    }}
                                    hierarchy={buttonHierarchy}
                                    key={value}
                                    onClick={() => {
                                      setSelectedOptions({
                                        ...selectedOptions,
                                        [optionType.name]: value,
                                      });
                                    }}
                                    pressed={isSelected}
                                    size="sm"
                                    text={value}
                                  />
                                );
                              })}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                  )}

                {/* Perks */}
                {perks && perks.length > 0 && (
                  <Flex dir="column" gap="xs">
                    {perks.map((perk, index) => (
                      <Flex align="center" gap="xs" key={index}>
                        <Text fontSize="sm" style={bodyStyles}>
                          ✓ {perk}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                )}
              </>
            )}
            {!subscriptionProductId && (
              <Text fontSize="sm" style={bodyStyles}>
                Select a product to start a subscription
              </Text>
            )}
          </Flex>
        </Flex>

        {/* Show plan selection dropdown if plans are available */}
        <Flex dir="column" gap="md">
          <Text fontSize="sm" fontWeight="medium" style={bodyStyles}>
            Select subscription plan
          </Text>
          <RedoSingleSelectDropdownInput
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
              ...dropdownStyles,
              fontFamily: bodyFontFamily,
            }}
            options={
              plansLoad.value?.map((plan) => ({
                id: plan.id.toString(),
                text: plan.planName,
                type: "text",
                value: plan.id.toString(),
              })) || []
            }
            optionSelected={(args) => {
              if (args.item.type === "text") {
                setSelectedPlanId(parseInt(args.item.value, 10));
              }
            }}
            placeholder="Choose a subscription plan"
            selectedItem={
              selectedPlanId
                ? {
                    id: selectedPlanId.toString(),
                    text:
                      plansLoad.value?.find((p) => p.id === selectedPlanId)
                        ?.planName || "",
                    type: "text",
                    value: selectedPlanId.toString(),
                  }
                : undefined
            }
            size="md"
          />
        </Flex>

        <RedoButton
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
            ...buttonStyles,
            ...buttonBorderRadius,
          }}
          disabled={
            !subscriptionProductId ||
            !selectedVariantId ||
            !selectedPlanId ||
            isCreatingSubscription
          }
          hierarchy={buttonHierarchy}
          onClick={handleStartSubscription}
          pending={isCreatingSubscription}
          size="lg"
          text={
            selectedPlanId && plansLoad.value
              ? plansLoad.value.find((p) => p.id === selectedPlanId)
                  ?.planName || "Start subscription"
              : "Start subscription"
          }
        />
      </Flex>
    </BlockWrapper>
  );
});
